import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronDown, ChevronUp, Check, X } from 'lucide-react';

/**
 * Shared benefits list - used across all upgrade modals
 * Medical-grade tone: calm, supportive, non-salesy
 */
const FULL_ACCESS_BENEFITS = [
  'Unlimited blood & nutrition tracking',
  'Full symptom tracking (beyond 6)',
  'Integrative Health guidance',
  'Advanced trends & insights',
  'Doctor-ready PDF exports',
  'Export history & saved ranges',
  'Ongoing education updates'
];

/**
 * WhatsIncluded - Inline expandable section showing full access benefits
 * Medical-grade tone: calm, supportive, non-salesy
 */
const WhatsIncluded = ({ expanded, onToggle }) => {
  const benefits = FULL_ACCESS_BENEFITS;

  return (
    <div className="mt-3">
      <button
        onClick={onToggle}
        className="text-sm text-slate-500 hover:text-primary transition-colors flex items-center gap-1 mx-auto"
      >
        See what&apos;s included
        {expanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>
      
      {expanded && (
        <div className="mt-4 pt-4 border-t border-slate-100 text-left">
          <p className="text-xs font-medium text-slate-700 mb-3">
            Full access includes:
          </p>
          <ul className="space-y-2">
            {benefits.map((benefit, idx) => (
              <li key={idx} className="flex items-start gap-2 text-xs text-slate-600">
                <Check className="h-3.5 w-3.5 text-teal-500 mt-0.5 flex-shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * SoftBlurModal - Portal-based modal for upgrade prompt
 * Supports ESC key to close, X button, and inline benefits expansion
 */
const SoftBlurModal = ({ isOpen, onClose }) => {
  const [showBenefits, setShowBenefits] = useState(false);
  
  // Handle ESC key to close modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 max-w-sm w-full mx-4 text-center max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        
        {/* Icon */}
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Sparkles className="h-6 w-6 text-primary" />
        </div>
        
        {/* Message - calm, supportive tone */}
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Free tracking limit reached
        </h3>
        <p className="text-sm text-slate-600 mb-5">
          Upgrade to continue tracking changes over time.
        </p>
        
        {/* Primary CTA */}
        <Link
          to="/upgrade"
          className="inline-block w-full bg-primary text-white font-medium py-3 px-6 rounded-xl hover:bg-primary/90 transition-colors"
        >
          Unlock full tracking
        </Link>
        
        {/* Inline expandable "What's included" */}
        <WhatsIncluded 
          expanded={showBenefits} 
          onToggle={() => setShowBenefits(!showBenefits)} 
        />
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

/**
 * SoftBlurWrapper - A gentle visual gating component for Free plan users
 * 
 * Shows a blurred overlay with upgrade prompt when free limit is reached.
 * Does NOT block content - just visually indicates premium features.
 * Modal can be closed with X button or ESC key. Blur remains after closing.
 * 
 * Props:
 * - isBlurred: Boolean to control blur state
 * - children: Content to wrap
 * - className: Additional classes for the wrapper
 * - showOverlay: Whether to show the upgrade overlay (default true)
 */
const SoftBlurWrapper = ({ 
  isBlurred = false, 
  children, 
  className = '',
  showOverlay = true 
}) => {
  const [modalDismissed, setModalDismissed] = useState(false);

  if (!isBlurred) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={`relative ${className}`}>
      {/* Blurred content */}
      <div 
        className="blur-sm opacity-60 pointer-events-none select-none"
        aria-hidden="true"
      >
        {children}
      </div>
      
      {/* Portal-based modal - closable with X or ESC */}
      {showOverlay && (
        <SoftBlurModal 
          isOpen={!modalDismissed} 
          onClose={() => setModalDismissed(true)} 
        />
      )}
    </div>
  );
};

export default SoftBlurWrapper;
